/**
 * VideoUploadInline - Inline video upload for StatCast Agent Panel
 *
 * ADK Multi-Agent: This component allows users to upload pitcher videos
 * for analysis (pitch tipping, biomechanics, etc.) directly in the
 * StatCast chat interface.
 *
 * Features:
 * - Drag and drop support
 * - File picker fallback
 * - Upload progress indicator
 * - Video preview thumbnail
 * - Gemini File API integration
 */

import { useState, useRef, useCallback } from 'react';
import { Badge } from '../ui/badge';

// Supported video formats
const SUPPORTED_FORMATS = [
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
];

// Max file size: 100MB (Gemini limit)
const MAX_FILE_SIZE = 100 * 1024 * 1024;

/**
 * Upload video to backend and get Gemini file URI
 */
async function uploadVideoToBackend(file) {
  const formData = new FormData();
  formData.append('file', file);

  // Use the existing file upload endpoint
  const response = await fetch('/api/files/upload', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Upload failed: ${error}`);
  }

  const result = await response.json();
  return result;
}

/**
 * Main VideoUploadInline Component
 */
export default function VideoUploadInline({
  onUploadComplete,
  onUploadError,
  disabled = false,
  compact = false,
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // Validate file
  const validateFile = useCallback((file) => {
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      return `Unsupported format: ${file.type}. Supported: MP4, WebM, MOV, AVI`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Max: 100MB`;
    }
    return null;
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    // Validate
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onUploadError?.(validationError);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress during upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload to backend
      const result = await uploadVideoToBackend(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Store uploaded video info
      const videoInfo = {
        name: file.name,
        size: file.size,
        type: file.type,
        uri: result.uri || result.fileUri || result.url,
        mimeType: result.mimeType || file.type,
        uploadedAt: new Date().toISOString(),
      };

      setUploadedVideo(videoInfo);

      // Notify parent
      onUploadComplete?.(videoInfo);

      console.log('[VideoUploadInline] Upload complete:', videoInfo);
    } catch (err) {
      console.error('[VideoUploadInline] Upload error:', err);
      setError(err.message);
      onUploadError?.(err.message);
    } finally {
      setIsUploading(false);
    }
  }, [validateFile, onUploadComplete, onUploadError]);

  // Drag handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled, isUploading, handleFileSelect]);

  // File input change
  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Trigger file picker
  const openFilePicker = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  // Clear uploaded video
  const clearVideo = useCallback(() => {
    setUploadedVideo(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Compact mode: just a button
  if (compact) {
    return (
      <div className="inline-flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileInputChange}
          className="hidden"
        />

        {uploadedVideo ? (
          <Badge variant="secondary" className="text-xs flex items-center gap-1">
            <span>🎬</span>
            {uploadedVideo.name.substring(0, 20)}...
            <button
              onClick={clearVideo}
              className="ml-1 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </Badge>
        ) : (
          <button
            onClick={openFilePicker}
            disabled={disabled || isUploading}
            className={`
              px-2 py-1 text-xs rounded-md border transition-colors
              ${isUploading
                ? 'bg-gray-100 text-gray-400 cursor-wait'
                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
              }
            `}
            title="Upload pitcher video for analysis"
          >
            {isUploading ? (
              <span className="flex items-center gap-1">
                <span className="animate-spin">⚾</span>
                {uploadProgress}%
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <span>🎬</span>
                Video
              </span>
            )}
          </button>
        )}
      </div>
    );
  }

  // Full mode: drop zone
  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Drop zone */}
      {!uploadedVideo && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFilePicker}
          className={`
            p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all
            ${isDragOver
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
            ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isUploading ? (
            <div className="text-center">
              <div className="animate-spin text-2xl mb-2">⚾</div>
              <p className="text-sm text-gray-600">Uploading... {uploadProgress}%</p>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                <div
                  className="bg-indigo-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-2xl mb-2">🎬</div>
              <p className="text-sm font-medium text-gray-700">
                Drop pitcher video here
              </p>
              <p className="text-xs text-gray-500 mt-1">
                or click to browse (MP4, MOV, WebM • Max 100MB)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Uploaded video preview */}
      {uploadedVideo && (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎬</span>
            <div>
              <p className="text-sm font-medium text-green-800">
                {uploadedVideo.name}
              </p>
              <p className="text-xs text-green-600">
                {(uploadedVideo.size / 1024 / 1024).toFixed(1)} MB • Ready for analysis
              </p>
            </div>
          </div>
          <button
            onClick={clearVideo}
            className="text-green-600 hover:text-red-600 transition-colors"
            title="Remove video"
          >
            ✕
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
